// Home — the default post-login landing (Dashboard is now reached separately
// from the sidebar for the bucket/mastery view). A weightage-first subject →
// topic → sub-topic browser: pick a subject, sort by how often a topic/
// sub-topic has appeared in past board papers, then per sub-topic get video/
// theory resources, a general AI tutor (no bucket gate — see
// components/home/AITutorTopicPanel.tsx), and a clear hand-off into Practice
// Mode and Exam Mode for that subject.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { boardGradeLabel, decodeClaims } from '@/lib/auth';
import type { PracticeTopic, SubjectDTO, SubTopicWithWeightage } from '@/types';
import { Button, StudentPage, StudentPageHeader } from '@/components/ds';
import { AITutorTopicPanel } from '@/components/home/AITutorTopicPanel';
import { FrequencyBadge } from '@/components/practice/FrequencyBadge';
import { WatchVideosButton } from '@/components/video/VideoLibrary';
import { frequencyTiers } from '@/lib/frequency';

type Sort = 'weightage' | 'name';

function pillClass(active: boolean): string {
  return `rounded-full px-2.5 py-1 text-xs font-semibold transition ${
    active ? 'bg-slate-900 text-white' : 'bg-paper-sunk text-ink-soft hover:text-ink'
  }`;
}

export function HomePage() {
  const claims = useMemo(() => decodeClaims(), []);
  const navigate = useNavigate();
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [topicId, setTopicId] = useState<string | null>(null);
  const [subtopicId, setSubtopicId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>('weightage');

  const subjectsQuery = useQuery({
    queryKey: ['subjects', claims?.board, claims?.grade],
    enabled: Boolean(claims?.board && claims?.grade),
    queryFn: () => apiGet<SubjectDTO[]>(`/subjects?board=${claims!.board}&grade=${claims!.grade}`),
  });

  // Same weightage signal Practice Mode's TopicPicker already sorts by.
  const topicsQuery = useQuery({
    queryKey: ['practice-topics'],
    queryFn: () => apiGet<PracticeTopic[]>('/practice/topics'),
  });

  const topics = useMemo(() => {
    const list = (topicsQuery.data ?? []).filter((t) => t.subjectId === subjectId);
    return sort === 'weightage'
      ? [...list].sort((a, b) => b.frequencyCount - a.frequencyCount)
      : [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [topicsQuery.data, subjectId, sort]);

  const subtopicsQuery = useQuery({
    queryKey: ['subtopics', topicId, claims?.board, claims?.grade],
    enabled: Boolean(topicId && claims?.board && claims?.grade),
    queryFn: () =>
      apiGet<SubTopicWithWeightage[]>(
        `/subtopics?topicId=${topicId}&board=${claims!.board}&grade=${claims!.grade}`,
      ),
  });

  const subtopics = useMemo(() => {
    const list = subtopicsQuery.data ?? [];
    return sort === 'weightage'
      ? [...list].sort((a, b) => b.frequencyCount - a.frequencyCount)
      : [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [subtopicsQuery.data, sort]);

  const topicTiers = useMemo(() => frequencyTiers(topics, (t) => t.frequencyCount), [topics]);
  const subtopicTiers = useMemo(
    () => frequencyTiers(subtopics, (s) => s.frequencyCount),
    [subtopics],
  );

  const resourcesQuery = useQuery({
    queryKey: ['subtopic-resources', subtopicId],
    enabled: Boolean(subtopicId),
    queryFn: () =>
      apiGet<{ theoryRefs: string[]; videoRefs: string[] }>(
        `/practice/subtopics/${subtopicId}/resources`,
      ),
  });

  const selectedTopic = topics.find((t) => t.topicId === topicId) ?? null;
  const selectedSubtopic = subtopics.find((s) => s.id === subtopicId) ?? null;
  const selectedSubject = subjectsQuery.data?.find((s) => s.id === subjectId) ?? null;
  const resources = resourcesQuery.data;
  const hasResources = Boolean(
    resources && (resources.theoryRefs.length > 0 || resources.videoRefs.length > 0),
  );
  const breadcrumbs = subjectId
    ? [
        {
          label: 'Subjects',
          onClick: () => {
            setSubjectId(null);
            setTopicId(null);
            setSubtopicId(null);
          },
        },
        {
          label: selectedSubject?.name ?? 'Subject',
          onClick: () => {
            setTopicId(null);
            setSubtopicId(null);
          },
        },
        ...(topicId
          ? [
              {
                label: selectedTopic?.name ?? 'Topic',
                onClick: () => setSubtopicId(null),
              },
            ]
          : []),
        ...(subtopicId ? [{ label: selectedSubtopic?.name ?? 'Sub-topic' }] : []),
      ]
    : undefined;
  const title =
    selectedSubtopic?.name ?? selectedTopic?.name ?? selectedSubject?.name ?? 'Subjects';

  return (
    <StudentPage>
      <StudentPageHeader
        breadcrumbs={breadcrumbs}
        title={title}
        subtitle={`${boardGradeLabel(claims)} · Pick a subject, then browse by how often it shows up in exams.`}
        action={
          subjectId ? (
            <span className="flex items-center gap-1.5">
              <button
                onClick={() => setSort('weightage')}
                className={pillClass(sort === 'weightage')}
              >
                By weightage
              </button>
              <button onClick={() => setSort('name')} className={pillClass(sort === 'name')}>
                A–Z
              </button>
            </span>
          ) : undefined
        }
      />

      {!subjectId ? (
        subjectsQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-paper-sunk" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(subjectsQuery.data ?? []).map((s) => (
              <button
                key={s.id}
                onClick={() => setSubjectId(s.id)}
                className="rounded-2xl border border-line bg-card p-4 text-left font-semibold text-ink shadow-sm transition hover:border-forest"
              >
                {s.name}
              </button>
            ))}
          </div>
        )
      ) : (
        <>
          {!topicId ? (
            <div className="flex flex-col gap-2">
              {topics.map((t) => (
                <button
                  key={t.topicId}
                  onClick={() => setTopicId(t.topicId)}
                  className="flex items-center justify-between gap-3 rounded-xl border border-line bg-card p-3 text-left transition hover:border-forest"
                >
                  <span className="font-medium text-ink">{t.name}</span>
                  <FrequencyBadge tier={topicTiers.get(t) ?? 'Low'} />
                </button>
              ))}
              {topics.length === 0 && (
                <p className="py-8 text-center text-sm text-ink-faint">
                  No topics yet for this subject.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {!subtopicId ? (
                <div className="flex flex-col gap-2">
                  {subtopics.map((st) => (
                    <button
                      key={st.id}
                      onClick={() => setSubtopicId(st.id)}
                      className="flex items-center justify-between gap-3 rounded-xl border border-line bg-card p-3 text-left transition hover:border-forest"
                    >
                      <span className="font-medium text-ink">{st.name}</span>
                      <FrequencyBadge tier={subtopicTiers.get(st) ?? 'Low'} />
                    </button>
                  ))}
                  {subtopics.length === 0 && (
                    <p className="py-8 text-center text-sm text-ink-faint">No sub-topics yet.</p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="rounded-2xl border border-line bg-card p-4">
                    <h3 className="mb-2 text-sm font-semibold text-ink">Video &amp; theory</h3>
                    {resourcesQuery.isLoading ? (
                      <div className="h-10 animate-pulse rounded-lg bg-paper-sunk" />
                    ) : !hasResources ? (
                      <p className="text-sm text-ink-faint">Nothing linked here yet.</p>
                    ) : (
                      <div className="flex flex-col gap-2 text-sm">
                        {resources?.theoryRefs.map((t, i) => (
                          <p
                            key={`theory-${i}`}
                            className="rounded-lg bg-paper-sunk px-3 py-2 text-ink"
                          >
                            {t}
                          </p>
                        ))}
                        {/* Played in the in-app library — a raw URL was what
                            showed here, and it left the product. */}
                        {resources?.videoRefs.map((v, i) => (
                          <WatchVideosButton
                            key={`video-${i}`}
                            source={{ videoRef: v }}
                            title={selectedSubtopic?.name ?? 'Videos'}
                            className="rounded-lg bg-paper-sunk px-3 py-2 text-left font-semibold text-forest hover:bg-paper-dim"
                          >
                            ▶{' '}
                            {resources.videoRefs.length > 1
                              ? `Watch videos (${i + 1})`
                              : `Watch videos on ${selectedSubtopic?.name ?? 'this sub-topic'}`}
                          </WatchVideosButton>
                        ))}
                      </div>
                    )}
                  </div>

                  <AITutorTopicPanel
                    subtopicId={selectedSubtopic!.id}
                    topicLabel={`${selectedTopic?.name ?? ''} — ${selectedSubtopic?.name ?? ''}`}
                  />

                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => navigate('/practice')}>Practice this subject →</Button>
                    <Button variant="secondary" onClick={() => navigate('/tests/past-papers')}>
                      Go to Tests &amp; papers →
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </StudentPage>
  );
}
